import { useCallback, useEffect, useState } from "react";
import classNames from "classnames";
import Cookies from "js-cookie";
import { useRouter } from "next/router";
import useDetectKeyboardOpen from "use-detect-keyboard-open";
import { motion } from "framer-motion";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import z from "zod";
import Image from "next/image";
//styles
import styles from "./otp.module.scss";
//component
import IconButton from "@/components/common/iconButton/iconButton";
import Input from "@/components/common/input/input";
import PrimaryButton from "@/components/common/primaryButton";
import { ResendTimmer } from "./ResendTimmer";
//api
import { VerifyOtp, VerifyOtpBody } from "@/apis/verifyOtp/verifyOtp";
import { ReSendOtpBody, resendOtp } from "@/apis/resendOtp/resendOtp";
import {
  VerifyOtpNewUser,
  VerifyOtpResponse,
} from "@/apis/verifyOtpNewUser/verifyOtpNewUser";
//types
import { OtpPageProps } from "./types";
import { AnimationType } from "../../loginmodal";
import { LoginStep } from "../../types";
import { ButtonType } from "@/components/common/primaryButton/types";
//context
import { useAppStore } from "@/providers/app";
//utils
import { identiyMixpanelUser, mixpanelTrack } from "@/helper/mixpanelService";
//zustand
import { useLoginStore } from "@/providers/login/login.provider";
// import { useAppStore } from "@/providers/app/zustand.provider";
import { NotificationType } from "@/components/common/notification/types";
import useViewportWidth from "@/hooks/useViewportWidth";
import { URLS } from "@/utils/baseUrl";
import { useIsMobile } from "@/helper/useIsMobile";
import { pushManualGTMEvent } from "@/utils/helpers/gtm";
import { userApi } from "@/services/api";

const schema = z.object({
  otp: z
    .string()
    .length(4, "Code must contain 4 digits") // ✅ Ensures length is 4
    .regex(/^\d{4}$/), // ✅ Ensures only numbers
});

type FormFields = z.infer<typeof schema>;

const Otp = ({ close }: OtpPageProps) => {
  const isKeyboardOpen = useDetectKeyboardOpen();
  const viewportWidth = useViewportWidth();

  //app provider
  const showNotification = useAppStore((state) => state.showNotification);
  const getUserData = useAppStore((state) => state.getUserData);
  const gameData = useAppStore((state) => state.gameData);
  //login provider
  const setState = useLoginStore((state) => state.setState);
  const phone = useLoginStore((state) => state.phone);
  const newUser = useLoginStore((state) => state.newUser);
  const animation = useLoginStore((state) => state.animationType);
  const setToastVisibility = useAppStore((state) => state.setToastVisibility);
  const isMobile = useIsMobile();

  const {
    register,
    handleSubmit,
    setError,
    formState: { isSubmitting, isValid, errors },
  } = useForm<FormFields>({
    defaultValues: {
      otp: "",
    },
    resolver: zodResolver(schema),
  });

  const router = useRouter();

  const [resent, setResent] = useState<boolean>(false);

  const handleClose = useCallback(() => {
    setState({
      animationType: AnimationType.SLIDEDOWN,
    });
    close && close();
  }, []);

  const resend = useCallback(async () => {
    try {
      let body: ReSendOtpBody = {
        phone: phone,
      };
      await userApi.sendOtp(body);
      setResent(true);
      setToastVisibility({
        text: "Code resent!",
        type: "PRIMARY",
      });
    } catch (e) {
      console.error("Resend otp error");
      setToastVisibility({
        text: "Oops, something went wrong. Please try again in a few minutes.",
        type: "SECONDARY",
      });
    }
  }, [phone]);

  const onHandle: SubmitHandler<FormFields> = useCallback(
    async (data) => {
      try {
        const body: VerifyOtpBody = {
          phone: phone,
          otp: data?.otp,
        };
        let response: VerifyOtpResponse | undefined;
        if (newUser) {
          // verifyNewPhone();
          response = await VerifyOtpNewUser(body);
          if (response?.data?.data?.token) {
            identiyMixpanelUser(response?.data?.data);
            if (response?.data?.data?.handle) {
              Cookies.set("ACCESS_TOKEN", response?.data?.data?.token);
              Cookies.set("_ID", response?.data?.data?._id);
              handleClose();
              const status = Cookies.get("PayJoinLogin");
              if (gameData && status) {
                router.push(`${URLS.GAME_CART}/${gameData?._id}`);
              }
            } else {
              setState({
                animationType: AnimationType.SLIDEINRIGHT,
                token: response?.data?.data?.token,
                id: response?.data?.data?._id,
                step:
                  viewportWidth > 430 ? LoginStep.NAME_EMAIL : LoginStep.NAME,
              });
            }
          }
        } else {
          // verifyPhone();
          response = await VerifyOtp(body);
          if (response?.data?.data?.token && response?.data?.data?._id) {
            Cookies.set("ACCESS_TOKEN", response?.data?.data?.token);
            Cookies.set("_ID", response?.data?.data?._id);
            identiyMixpanelUser(response?.data?.data);
            // actions?.getUserData(response?.data?.data?.token);
            getUserData(response?.data?.data?.token);
          }

          if (response?.data?.data?.token) {
            const status = Cookies.get("PayJoinLogin");
            if (gameData && status) {
              router.push(`${URLS.GAME_CART}/${gameData?._id}`);
            }
          }
          pushManualGTMEvent("login_status", "success", {
            id: response?.data?.data?._id,
          });
          handleClose();
        }

        mixpanelTrack("Signup - OTP Entered", {
          phone: phone,
          newUser,
        });
        identiyMixpanelUser(response?.data?.data);
      } catch (e) {
        setError("otp", { type: "manual", message: "Invalid Code" });
        console.error("new verification err", e);
        setToastVisibility({
          text: "Oops, something went wrong. Please try again in a few minutes.",
          type: "SECONDARY",
        });
      }
    },
    [phone, newUser]
  );

  const back = useCallback(() => {
    setState({
      animationType: AnimationType.SLIDEINLEFT,
      step: LoginStep.PHONE,
    });
  }, []);

  return (
    <motion.form
      className={styles["container"]}
      initial={{
        x: animation === AnimationType.SLIDEINRIGHT ? "100%" : "-100%",
      }}
      animate={{ x: "0%" }}
      transition={{ type: "spring", damping: 20, stiffness: 100 }}
      exit={{ x: animation === AnimationType.SLIDEDOWN ? "0%" : "-100%" }}
      onSubmit={handleSubmit(onHandle)}
    >
      <div className={styles["header"]}>
        <>
          <Image
            onClick={() => {
              back();
            }}
            height={22}
            width={22}
            src={"/icons/arrow-left.svg"}
            alt={"close-icon"}
            className={styles["arrow"]}
          />
        </>
        <p>Login / Signup to continue</p>
        <div />
      </div>
      <div className={classNames(styles["title-cont"])}>
        <div className={classNames(styles["mobile"])}>
          <p>Verify your number</p>
          <span>Enter the code we’ve sent by text to</span>
          <span>+91 {phone}</span>
        </div>
        <div className={classNames(styles["web"])}>
          <p>Enter the verification code sent</p>
          <span>Check your texts on +91 {phone} for a code.</span>
        </div>
      </div>
      <div className={classNames(styles["ph-24"])}>
        <Input
          type={"number"}
          placeholder="- - - -"
          errorText={!isMobile ? errors?.otp?.message : ""}
          register={register("otp")}
          inputMode="numeric"
          pattern="[0-9]*"
          onInput={(e) => {
            const input = e.target as HTMLInputElement; // Cast to HTMLInputElement
            input.value = input.value.slice(0, 4);
          }}
        />

        {isMobile && errors?.otp?.message && (
          <div className={classNames(styles["mt-16"], styles["relative"])}>
            <p className={classNames(styles.errorText)}>
              {errors?.otp?.message}
            </p>
          </div>
        )}
      </div>

      <div
        className={classNames(
          styles["button_container"],
          isKeyboardOpen && styles["bottom-45"]
        )}
      >
        <p>
          Haven’t received a code?{" "}
          {resent ? (
            <ResendTimmer
              onComplete={() => {
                setResent(false);
              }}
            />
          ) : (
            <span onClick={resend}>Tap to resend.</span>
          )}
        </p>
        {/* {resent && (
          <p className={styles["code-sent-text"]}>Code sent successfully</p>
        )} */}
        <div className={styles["circular-button"]}>
          <IconButton
            loading={isSubmitting}
            disabled={!isValid || isSubmitting}
            type="submit"
          />
        </div>
        <div className={styles["full-button"]}>
          <PrimaryButton
            buttonType={!isValid ? ButtonType.DISABLED : ButtonType.PRIMARY}
            text={"Continue"}
            type="submit"
          />
        </div>
      </div>
    </motion.form>
  );
};

export default Otp;
